import { useRef, useState } from 'react';
import { Tooltip } from '@mui/material';
import { useDispatch } from '../../../../redux/store';
import { getInvoices } from '../../../../redux/slices/invoicing';
import { getInvoiceProcessingQueue } from '../../../../api/invoicing';
import { STATUS_LABEL_MAP, isCanceledParsingStatus, getErrorType, isFailedParsingStatus } from './ParsingStatusUtils';
import { fMinDate, fDateTime } from '../../../../utils/formatTime';

export const ParsingStatusLabel = ({ processingStatus, invoiceId, onRetry, disabled }) => {
  const [queueItem, setQueueItem] = useState(null);
  const [open, setOpen] = useState(false);
  const errorMessage = getErrorType(queueItem?.errorMessage);
  const dispatch = useDispatch();

  const hoverTimer = useRef(null);

  const handleMouseEnter = () => {
    hoverTimer.current = setTimeout(async () => {
      setOpen(true);
      try {
        const response = await getInvoiceProcessingQueue(invoiceId);
        if (response.status === 200) {
          setQueueItem(response.data);
          if (response.data.status !== processingStatus) {
            dispatch(getInvoices(true, [invoiceId]));
          }
        }
      } catch (err) {
        console.error('Failed to fetch queue item', err);
      }
    }, 300); // only fires if user stays for 400ms
  };

  const handleMouseLeave = () => {
    clearTimeout(hoverTimer.current); // cancel if just passing through
    setOpen(false);
  };

  const handleClose = () => setOpen(false);
  const tooltipContent =
    queueItem &&
    queueItem?.invoiceId === invoiceId &&
    (fMinDate(queueItem.enqueuedAt) ||
      queueItem.startedAt ||
      queueItem.completedAt ||
      queueItem.modifiedBy ||
      errorMessage) ? (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {fMinDate(queueItem.enqueuedAt) && <span>Queued At: {fDateTime(queueItem.enqueuedAt)}</span>}
        {queueItem.startedAt && <span>Started At: {fDateTime(queueItem.startedAt)}</span>}
        {queueItem.completedAt && (
          <span>
            {isCanceledParsingStatus(processingStatus) ? 'Canceled' : 'Completed'} At:{' '}
            {fDateTime(queueItem.completedAt)}
          </span>
        )}
        {queueItem.modifiedBy && isCanceledParsingStatus(processingStatus) && (
          <span>Canceled By: {queueItem.modifiedBy}</span>
        )}
        {errorMessage && <span style={{ color: '#ff6b6b' }}>Error: {errorMessage}</span>}
      </div>
    ) : isFailedParsingStatus(processingStatus) && disabled ? (
      'Insufficient permissions for this facility'
    ) : null;

  const handleRetry = (e) => {
    e.stopPropagation();
    onRetry?.(invoiceId);
  };
  const label = STATUS_LABEL_MAP[processingStatus]?.(handleRetry, disabled);
  if (!label) return null;

  return (
    <Tooltip
      title={tooltipContent}
      open={open}
      onClose={handleClose}
      disableFocusListener
      disableTouchListener
      disableHoverListener={false}
    >
      <span onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        {label}
      </span>
    </Tooltip>
  );
};
